import React from 'react';
import { Box, Flex, HStack, Text, Tag, IconButton } from '@chakra-ui/react';
import { Handle, Position } from 'reactflow';
import {
    FiSend,
    FiEdit,
    FiCopy,
    FiTrash2,
} from 'react-icons/fi';
import { LuBell, LuMail, LuMessageCircle, LuMessageSquare, LuPhone, LuMerge } from 'react-icons/lu';

const ActionNode = ({ data }) => {
    const Channels = [
        {
            name: "Whatsapp",
            icon: LuMessageCircle,
            color: "#00a63e",
            description: "Rich messaging with media and buttons",
            id: 2,
        },
        {
            name: "SMS",
            icon: LuMessageSquare,
            color: "#155dfc",
            description: "Simple text messages",
            id: 1,
        },
        {
            name: "E-Mail",
            icon: LuMail,
            color: "#9810fa",
            description: "HTML emails with rich content",
            id: 3,
        },
        {
            name: "Push Notification",
            icon: LuBell,
            color: "#e60076",
            description: "Mobile app notifications",
            id: 6,
        },
        {
            name: "In-App Notification",
            icon: LuBell,
            color: "#4f39f6",
            description: "In-app messages and alerts",
            id: 5,
        },
        {
            name: "Voice (IVR)",
            icon: LuPhone,
            color: "#f54a00",
            description: "Automated voice calls",
            id: 4,
        },
    ];

    const getChannelInfo = (channelId) => {
        // Check for smart routing
        if (channelId === 'smart') {
            return {
                icon: LuMerge,
                name: "Smart Routing",
                color: "#9810fa"
            };
        }
        
        return (
            Channels.find(ch => ch.id === Number(channelId)) || {
                icon: FiSend,
                name: "Message",
                color: "var(--brand-primary)"
            }
        );
    };

    const channelInfo = getChannelInfo(data.channel);
    const IconComponent = channelInfo.icon;

    return (
        <Box
            bg="white"
            borderWidth="2px"
            borderColor="var(--brand-primary)"
            borderRadius="xl"
            boxShadow={data.selected ? 'xl' : 'lg'}
            minW="240px"
            width={"280px"}
        >
            <Handle type="target" position={Position.Left} style={{ background: 'black' }} />

            <Flex
                bg="var(--brand-primary)"
                color="white"
                px={4}
                py={2.5}
                borderTopRadius="xl"
                align="center"
                justify="space-between"
            >
                <HStack spacing={2}>
                    <FiSend />
                    <Text noOfLines={1} fontSize="sm" fontWeight="medium">Message</Text>
                </HStack>
                <HStack spacing={1}>
                    <IconButton
                        size="xs"
                        variant="ghost"
                        color="white"
                        icon={<FiEdit />}
                        onClick={(e) => { e.stopPropagation(); data.onEdit?.(data.id); }}
                    />
                    <IconButton
                        size="xs"
                        variant="ghost"
                        color="white"
                        icon={<FiCopy />}
                        onClick={(e) => { e.stopPropagation(); data.onDuplicate?.(data.id); }}
                    />
                    <IconButton
                        size="xs"
                        variant="ghost"
                        color="white"
                        icon={<FiTrash2 />}
                        onClick={(e) => { e.stopPropagation(); data.onDelete?.(data.id); }}
                    />
                </HStack>
            </Flex>

            <Box p={3.5}>
                <Text fontSize="sm" fontWeight="medium" mb={1.5}>
                    {data.label || 'Send Message'}
                </Text>

                {/* Message Type Badge */}
                {data.messageType && (
                    <Tag
                        fontSize="9px"
                        size="sm"
                        mb={2}
                        mr={2}
                        bg={data.messageType === 'template' ? 'blue.50' : 'green.50'}
                        color={data.messageType === 'template' ? 'blue.700' : 'green.700'}
                        border="1px"
                        borderColor={data.messageType === 'template' ? 'blue.200' : 'green.200'}
                    >
                        {data.messageType === 'template' ? 'Template' : 'Freeform'}
                    </Tag>
                )}

                {/* Channel Tag */}
                {data.channel ? (
                    <Tag 
                        size="sm" 
                        bg={channelInfo.color} 
                        mt={data.messageType ? 0 : 2} 
                        mb={2} 
                        color={"white"}
                    >
                        <IconComponent style={{ marginRight: '4px' }} />
                        {channelInfo.name}
                    </Tag>
                ) : (
                    <Text fontSize="xs" color="gray.500" mt={2} mb={2} fontStyle="italic">
                        Channel not configured
                    </Text>
                )}

                {/* Template or Freeform Message Preview */}
                {data.messageType === 'template' && data.template && (
                    <Text fontSize="xs" color="gray.600" mt={1.5} noOfLines={2}>
                        Template: {data.template}
                    </Text>
                )}

                {data.messageType === 'freeform' && data.freeformMessage && (
                    <Text fontSize="xs" color="gray.600" mt={1.5} noOfLines={2}>
                        Message: {data.freeformMessage}
                    </Text>
                )}

                {/* Fallback for old nodes without messageType */}
                {!data.messageType && data.template && (
                    <Text fontSize="xs" color="gray.600" mt={1.5} noOfLines={2}>
                        Template: {data.template}
                    </Text>
                )}
            </Box>

            <Handle type="source" position={Position.Right} style={{ background: 'black' }} />
        </Box>
    );
};

export default ActionNode;